import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import WaterfallCard from './WaterfallCard';
/** Read once per page: the admin setting cannot change without a reload. */
let cardGutter = null;
function cardGutterSetting() {
  return cardGutter ??= app.forum.attribute('waterfallCardGutter') ?? 12;
}

/**
 * Responsive card grid. Every card keeps the same portrait cover frame, so
 * rows line up regardless of the original image ratio, and the column count is
 * left entirely to the stylesheet's fluid tracks (no breakpoints here).
 *
 * Layout is delegated to CSS grid and the browser's native lazy-loading, so
 * there is no manual packing or virtual scrolling to keep in sync. The next
 * page is fetched by an IntersectionObserver sentinel rendered *after* the
 * grid, which also keeps the "end of feed" marker below the last row instead
 * of absolutely positioned on top of the cards.
 */
export default class WaterfallGrid extends Component {
  observer = null;
  sentinel = null;
  oncreate(vnode) {
    super.oncreate(vnode);
    this.observeSentinel();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    this.observeSentinel();
  }
  onremove(vnode) {
    super.onremove(vnode);
    this.observer?.disconnect();
    this.observer = null;
    this.sentinel = null;
  }

  /**
   * Observe the bottom sentinel so the next page loads slightly before the
   * user reaches it. Re-observing only when the element changes keeps the
   * observer alive across ordinary redraws.
   */
  observeSentinel() {
    const sentinel = this.element?.querySelector('.WaterfallGrid-sentinel') ?? null;
    if (!sentinel) {
      this.observer?.disconnect();
      this.observer = null;
      this.sentinel = null;
      return;
    }
    if (this.sentinel === sentinel) {
      return;
    }
    this.observer?.disconnect();
    this.sentinel = sentinel;
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.attrs.state.loadNext();
      }
    },
    // Start fetching well before the user reaches the bottom.
    {
      rootMargin: '600px 0px'
    });
    this.observer.observe(sentinel);
  }
  view() {
    const {
      state
    } = this.attrs;
    if (state.loading) {
      return m("div", {
        className: "WaterfallGrid"
      }, m("div", {
        className: "WaterfallGrid-state WaterfallGrid-state--loading"
      }, m(LoadingIndicator, {
        size: "large"
      }), m("p", null, app.translator.trans('lcoy-waterfall.forum.grid.loading'))));
    }
    const sets = state.sets;
    if (sets.length === 0) {
      return m("div", {
        className: "WaterfallGrid"
      }, m("div", {
        className: "WaterfallGrid-state WaterfallGrid-state--empty"
      }, m("div", {
        className: "WaterfallGrid-emptyArt",
        "aria-hidden": "true"
      }, m("i", {
        className: "far fa-image"
      }), m("i", {
        className: "far fa-images"
      })), m("h3", null, app.translator.trans('lcoy-waterfall.forum.grid.empty_title')), m("p", null, app.translator.trans('lcoy-waterfall.forum.grid.empty_text'))));
    }
    return m("div", {
      className: "WaterfallGrid"
    }, m("div", {
      className: "WaterfallGrid-grid",
      style: {
        '--waterfall-gutter': `${cardGutterSetting()}px`
      }
    }, sets.map(set => m(WaterfallCard, {
      key: set.id(),
      set: set,
      onclick: () => this.attrs.onOpen(set),
      onDelete: deleted => state.removeSet(deleted)
    }))), state.hasMore ? m("div", {
      className: "WaterfallGrid-sentinel",
      "aria-hidden": "true"
    }, state.loadingMore && m(LoadingIndicator, {
      size: "small"
    })) : m("div", {
      className: "WaterfallGrid-end"
    }, app.translator.trans('lcoy-waterfall.forum.grid.end_of_feed')));
  }
}
flarum.reg.add('lcoy-waterfall', 'forum/components/WaterfallGrid', WaterfallGrid);